export interface AppData {
  id: string;
  name: string;
  tagline: string;
  description: string;
  accentColor: string;
  logo: string;
  platform: 'web' | 'ios' | 'both';
  status: 'live' | 'beta' | 'coming-soon';
  category: 'productivity' | 'lifestyle' | 'developer';
  features: string[];
  techStack: string[];
  imageLaptop?: string;
  imagePhone?: string;
  links: {
    web?: string;
    appStore?: string;
    github?: string;
  };
}

export const apps: AppData[] = [
  // Lifestyle Apps
  {
    id: 'charque',
    name: 'Charque',
    tagline: 'Smart Meal Planning',
    description: 'A comprehensive meal planning ecosystem with web and iOS apps. Plan weekly meals, manage grocery lists, discover recipes, and track home food inventory. Built for families who want to reduce food waste and simplify dinner decisions.',
    accentColor: '#A35229',
    logo: '/assets/charque-logo.png',
    platform: 'both',
    status: 'live',
    category: 'lifestyle',
    features: [
      'Weekly meal calendar with drag-and-drop',
      'Smart grocery list generation',
      'Recipe discovery and management',
      'Home food inventory tracking',
      'Family sharing and collaboration',
      'Cross-device sync via Firebase',
    ],
    techStack: ['React', 'TypeScript', 'Firebase', 'Swift', 'SwiftUI'],
    links: {
      web: 'https://charque.app',
      appStore: '#',
    },
  },
  {
    id: 'bestagon',
    name: 'The Bestagon',
    tagline: 'Hexagonal Puzzle Game',
    description: 'An addictive iOS puzzle game featuring hexagonal tiles. Match colors, clear patterns, and compete for high scores. Simple to learn, challenging to master. Because hexagons are the bestagons.',
    accentColor: '#34c759',
    logo: '/assets/bestagon-logo.png',
    platform: 'ios',
    status: 'live',
    category: 'lifestyle',
    features: [
      'Unique hexagonal gameplay',
      'Multiple difficulty modes',
      'Daily challenges',
      'Global leaderboards',
      'Achievement system',
      'Offline play',
    ],
    techStack: ['Swift', 'SwiftUI', 'GameKit'],
    links: {
      appStore: '#',
    },
  },
  {
    id: 'tides',
    name: 'Tides & UV',
    tagline: 'Real-time tides and UV for the Ayampe coast',
    description: 'A focused tool we built for real use on the Ecuadorian Pacific coast — a native SwiftUI iOS app and companion web dashboard surfacing tide times, tide heights, and hourly UV index for a single stretch of shoreline near Ayampe. One location, done exceptionally: sunlight-legible typography, sensible defaults, and a live backend that refreshes the forecast every day. A deliberate scope decision — built to do one thing beautifully rather than a generic tides product spread thin.',
    accentColor: '#0a84ff',
    logo: '/assets/tides-logo.svg',
    platform: 'both',
    status: 'live',
    category: 'lifestyle',
    features: [
      'High & low tide times with heights',
      'Hourly UV index with peak callout',
      'Sunrise, sunset, and moon phase',
      'Four-day outlook at a glance',
      'Native SwiftUI iOS client',
      'Live backend with daily refresh',
    ],
    techStack: ['Swift', 'SwiftUI', 'React', 'TypeScript', 'Vite'],
    imageLaptop: '/assets/tides-web-hero.png',
    imagePhone: '/assets/tides-ios-hero.png',
    links: {
      web: 'https://tides.data26.solutions',
    },
  },

  // Productivity Apps
  {
    id: 'what',
    name: 'What',
    tagline: 'Track Your Things',
    description: 'Personal inventory management for the organized household. Catalog your belongings, track locations, manage warranties, and never lose track of important items again. Perfect for moving, insurance documentation, or just staying organized.',
    accentColor: '#007aff',
    logo: '/assets/inventory-logo.png',
    platform: 'both',
    status: 'live',
    category: 'productivity',
    features: [
      'Hierarchical location organization',
      'Photo documentation',
      'Search and filtering',
      'Category tagging',
      'Value tracking for insurance',
      'Export capabilities',
    ],
    techStack: ['React', 'TypeScript', 'Firebase', 'Swift', 'SwiftUI'],
    links: {
      web: 'https://inventory.lancastersystems.dev',
      appStore: '#',
    },
  },
  {
    id: 'when',
    name: 'When',
    tagline: 'Event Planning Made Simple',
    description: 'Coordinate events and plans with friends and family. Create events, share availability, poll for preferences, and make group decisions easier. No more endless group chats trying to find a time that works.',
    accentColor: '#511461',
    logo: '/assets/when-logo.png',
    platform: 'web',
    status: 'live',
    category: 'productivity',
    features: [
      'Event creation and sharing',
      'Availability polling',
      'Preference voting',
      'Guest management',
      'Calendar integration',
      'Real-time updates',
    ],
    techStack: ['React', 'TypeScript', 'Firebase', 'Vite'],
    links: {
      web: 'https://when.lancastersystems.dev',
    },
  },
  {
    id: 'who',
    name: 'Who',
    tagline: 'Contact & Relationship Manager',
    description: 'Keep track of the people in your life. Remember birthdays, manage relationships, and never forget important details about the people who matter most.',
    accentColor: '#ff9500',
    logo: '/assets/whoLogo.png',
    platform: 'web',
    status: 'coming-soon',
    category: 'productivity',
    features: [
      'Contact organization',
      'Relationship tracking',
      'Birthday reminders',
      'Interaction history',
      'Notes and details',
      'Smart suggestions',
    ],
    techStack: ['React', 'TypeScript', 'Firebase'],
    links: {},
  },
  {
    id: 'how',
    name: 'How',
    tagline: 'Personal Knowledge Base',
    description: 'Your second brain for capturing and organizing knowledge. Save how-tos, tutorials, and procedures so you never have to figure out the same thing twice.',
    accentColor: '#af52de',
    logo: '/assets/howLogo.png',
    platform: 'web',
    status: 'coming-soon',
    category: 'productivity',
    features: [
      'Step-by-step guides',
      'Knowledge organization',
      'Search and tagging',
      'Media attachments',
      'Share with others',
      'Offline access',
    ],
    techStack: ['React', 'TypeScript', 'Firebase'],
    links: {},
  },

  // Developer Tools
  {
    id: 'substrate',
    name: 'Substrate',
    tagline: "Lancaster Systems Automation's autonomous software-development system",
    description:
      "Substrate is Data 26's autonomous software-development system. It takes a written specification and produces deployed, working software — directed by a human, without manual coding. Demonstrated on full-stack web applications and native iOS clients, designed and deployed live.",
    accentColor: '#00d4aa',
    logo: '/assets/substrate-logo.svg',
    platform: 'web',
    status: 'beta',
    category: 'developer',
    features: [
      'Specification to deployed software',
      'Full-stack web applications',
      'Native iOS clients',
      'Human-directed, without manual coding',
    ],
    techStack: [],
    links: {},
  },
];

// Get apps by category
export const lifestyleApps = apps.filter(app => app.category === 'lifestyle');
export const productivityApps = apps.filter(app => app.category === 'productivity');
export const developerApps = apps.filter(app => app.category === 'developer');

// Featured apps for homepage (live apps only)
export const featuredApps = apps.filter(app => app.status === 'live');

export function getAppById(id: string): AppData | undefined {
  return apps.find(app => app.id === id);
}

export function getAppsByCategory(category: AppData['category']): AppData[] {
  return apps.filter(app => app.category === category);
}
